import AppBridgeHelper from "./AppBridgeHelper";
import * as InvoiceHelper from "./InvoiceHelper";
import { bytesToBase64 } from "helpers/StringHelper";
import { eventsEmitter } from "utils/events";
import {
  INVOICE_FORMATS,
  KOT_FORMAT,
  PRINTER_TYPES,
  KOT_MESSAGES,
  KOT_TITLES,
  KOT_SPLIT_OPTIONS,
  KOT_STATUS,
  EVENTS,
} from "const";
import * as TerminalHelper from "helpers/TermialHelper";
import * as ProductHelper from "helpers/ProductHelper";
import * as SalesHelper from "helpers/SalesHelper";
import DateTimeHelpers from "helpers/DateTimeHelpers";
import toast from "react-hot-toast";
import { PrinterManager } from "managers/PrinterManager";

function getKitchenIp(category) {
  const ip = category?.kitchen_ip;
  return ip && ip.trim() !== "" ? ip.trim() : "NO_IP";
}

function getKitchenName(category) {
  const name = category?.kitchen_name;
  return name && name.trim() !== "" ? name.trim() : "NO_NAME";
}

function getKitchenGroupKey(saleItem, categoryMap, splitOption) {
  const category = categoryMap[String(saleItem.item?.category)];

  if (splitOption === KOT_SPLIT_OPTIONS.KOT_SPLIT_BY_CATEGORY.value) {
    return String(category?.id || "NO_CATEGORY");
  }

  return `${getKitchenName(category)}_${getKitchenIp(category)}`;
}

function buildKotPrintJobsByItem(sale, categoryMap) {
  const printJobs = [];
  (sale.items ?? []).forEach((saleItem) => {
    const category = categoryMap[String(saleItem.item?.category)];
    printJobs.push({
      kitchen_ip: getKitchenIp(category),
      kitchen_name: category?.kitchen_name || null,
      items: [saleItem],
    });
  });
  return printJobs;
}

function buildKotPrintJobsByItemQty(sale, categoryMap) {
  const printJobs = [];
  (sale.items ?? []).forEach((saleItem) => {
    const category = categoryMap[String(saleItem.item?.category)];
    const kitchenIp = getKitchenIp(category);
    const kitchenName = category?.kitchen_name ?? null;
    const itemQty = Number(saleItem.qty ?? 0);
    const wholeQty = Math.floor(itemQty);

    for (let i = 0; i < wholeQty; i++) {
      printJobs.push({
        kitchen_ip: kitchenIp,
        kitchen_name: kitchenName,
        items: [{ ...saleItem, qty: 1 }],
      });
    }
    const remainder = itemQty - wholeQty;
    if (remainder > 0) {
      printJobs.push({
        kitchen_ip: kitchenIp,
        kitchen_name: kitchenName,
        items: [{ ...saleItem, qty: remainder }],
      });
    }
  });
  return printJobs;
}

function buildKotNotificationJobsPerKitchen(sale, categoryMap) {
  const groups = {};
  (sale.items ?? []).forEach((saleItem) => {
    const category = categoryMap[String(saleItem.item?.category)];
    const kitchenName = getKitchenName(category);
    const kitchenIp = getKitchenIp(category);
    const groupKey = `${kitchenName}_${kitchenIp}`;
    if (!groups[groupKey]) {
      groups[groupKey] = {
        kitchen_ip: kitchenIp,
        kitchen_name: category?.kitchen_name || null,
        items: [],
      };
    }
  });
  return Object.values(groups);
}

function buildKotPrintJobs(splitOption, sale, categoryMap, showItems = true) {
  if (!showItems) {
    return buildKotNotificationJobsPerKitchen(sale, categoryMap);
  }

  if (splitOption === KOT_SPLIT_OPTIONS.KOT_SPLIT_BY_ITEM.value) {
    return buildKotPrintJobsByItem(sale, categoryMap);
  }

  if (splitOption === KOT_SPLIT_OPTIONS.KOT_SPLIT_BY_CATEGORY.value) {
    const groups = {};
    const addToGroup = (saleItem) => {
      const category = categoryMap[String(saleItem.item?.category)];
      if (!groups[category?.id]) {
        groups[category?.id] = {
          kitchen_ip: getKitchenIp(category),
          kitchen_name: category?.kitchen_name ?? null,
          items: [],
        };
      }
      groups[category?.id].items.push(saleItem);
    };
    (sale.items ?? []).forEach((saleItem) => addToGroup(saleItem));
    return Object.values(groups);
  }

  if (splitOption === KOT_SPLIT_OPTIONS.KOT_SPLIT_BY_ITEM_QTY.value) {
    return buildKotPrintJobsByItemQty(sale, categoryMap);
  }

  const groups = {};
  const addToGroup = (saleItem) => {
    const category = categoryMap[String(saleItem.item?.category)];
    const kitchenName = getKitchenName(category);
    const kitchenIp = getKitchenIp(category);
    const groupKey = `${kitchenName}_${kitchenIp}`;
    if (!groups[groupKey]) {
      groups[groupKey] = {
        kitchen_ip: kitchenIp,
        kitchen_name: category?.kitchen_name || null,
        items: [],
      };
    }
    groups[groupKey].items.push(saleItem);
  };
  (sale.items ?? []).forEach((saleItem) => addToGroup(saleItem));
  return Object.values(groups);
}

async function generateKotText(subSale, titles, invoiceFormat, notes = [], strock = false) {
  if (invoiceFormat === KOT_FORMAT.THREE_INCH_KOT_MULTI_LANGUAGE) {
    return await InvoiceHelper.getMultiLanguageThreeInchKOT(subSale, titles, { notes }, strock);
  }
  if (invoiceFormat === KOT_FORMAT.THREE_INCH_KOT_MULTI_LANGUAGE_ARABIC) {
    return await InvoiceHelper.getMultiLanguageThreeInchKOT(subSale, titles, { isArabic: true, notes }, strock);
  }
  if (invoiceFormat === KOT_FORMAT.THREE_INCH_KOT_LARGE) {
    return await InvoiceHelper.getThreeInchLargeKOT(subSale, titles, notes, strock);
  }
  return await InvoiceHelper.getThreeInchKOT(subSale, titles, notes, strock);
}

async function executeKotPrintJobs(
  printJobs,
  sale,
  titles,
  invoiceFormat,
  terminalSettings,
  notes = [],
  strock = false,
) {
  const { printerType: defaultPrinterType, printerTarget: defaultPrinterTarget } =
    TerminalHelper.getDefaultPrinterConfig(terminalSettings);

  for (const { kitchen_ip, kitchen_name, items } of printJobs) {
    try {
      const subSale = { ...sale, items, kitchen_name, notes };
      const text = await generateKotText(subSale, titles, invoiceFormat, notes, strock);
      const base64Data = bytesToBase64(text);

      const isDefaultPrinter = kitchen_ip === "NO_IP" || !kitchen_ip || kitchen_ip.trim() === "";
      const pType = isDefaultPrinter ? defaultPrinterType : PRINTER_TYPES.LAN;
      const pTarget = isDefaultPrinter ? defaultPrinterTarget : kitchen_ip;

      await PrinterManager.print({
        content: base64Data,
        printerType: pType,
        printerTarget: pTarget,
        label: `KOT ${sale.local_id}`,
      });
    } catch (error) {
      toast.error(`Failed to print KOT for ${kitchen_name || ""} kitchen, ${error.message}`);
    }
  }
}

async function printConsolidatedKotIfNeeded(
  sale,
  skipConsolidatedKOT,
  terminalSettings,
  invoiceFormat,
  defaultKotIp,
  notes = [],
  titles = [KOT_TITLES.CONSOLIDATED],
  strock = false,
) {
  if (!terminalSettings.consolidatedKOT || skipConsolidatedKOT) return;

  const consolidatedIp = terminalSettings.consolidatedKOTIp;
  const text = await generateKotText(sale, titles, invoiceFormat, notes, strock);
  const base64Data = bytesToBase64(text);

  let consIp = consolidatedIp || defaultKotIp;
  const { printerType: defaultPrinterType, printerTarget: defaultPrinterTarget } =
    TerminalHelper.getDefaultPrinterConfig(terminalSettings);

  const isConsolidatedIpAvailable = consIp !== "NO_IP" && consIp && consIp.trim() !== "";
  const pType = isConsolidatedIpAvailable ? PRINTER_TYPES.LAN : defaultPrinterType;
  const pTarget = isConsolidatedIpAvailable ? consIp : defaultPrinterTarget;
  try {
    await PrinterManager.print({
      content: base64Data,
      printerType: pType,
      printerTarget: pTarget,
      label: `CONSOLIDATED KOT ${sale.local_id}`,
    });
  } catch (error) {
    console.error(`Error printing consolidated KOT for "${pTarget || "Default"} printer":`, error);
    toast.error(`Failed to print consolidated KOT for "${pTarget || "Default"} printer", ${error.message}`);
  }
}

function formatReducedItemsNotes(reducedItems = []) {
  if (!reducedItems || reducedItems.length === 0) return [];
  return [
    "Reduced Items\n" +
      reducedItems
        .map(
          (it) => `- ${it.name} ${it.option?.label ? ` - ${it.option.label}` : ""} ${it.kotPrintedQty} -> ${it.newQty}`,
        )
        .join("\n") +
      "\n",
  ];
}

class KotManager {
  handleTokenAssignment(sale) {
    const settings = TerminalHelper.getTerminalSettings();
    const showTokenNumber = settings.showTokenNumber;

    if (!showTokenNumber) return { token_no: null, shouldIncrementToken: false };

    if (sale.token_no) return { token_no: sale.token_no, shouldIncrementToken: false };

    const localTokenNumber = TerminalHelper.getLocalTokenNumber();
    const token_no = String(localTokenNumber);

    // Increment and save for next time
    TerminalHelper.saveLocalTokenNumber(Number(token_no) + 1);
    // TODO: Emit corresponding event only, don't send generic event that cause a big refresh
    eventsEmitter.emit(EVENTS.TERMINAL_SETTINGS_UPDATED);

    return { token_no, shouldIncrementToken: true };
  }

  extractKotChanges(activeSale) {
    const existingItems = activeSale?.items || [];
    const deletedItems = activeSale?.deletedItems || [];

    // 1. Items reduced in active items (kotPrintedQty > qty)
    const reducedActiveItems = existingItems
      .filter((item) => item.kotPrintedQty > (item.qty || 0))
      .map((item) => {
        const newQty = Math.max(0, item.qty || 0);
        return {
          ...item,
          newQty,
          qty: item.kotPrintedQty - newQty,
        };
      });

    // 2. Items completely deleted from cart that were printed on KOT
    const removedCartItems = deletedItems
      .filter((item) => (item.kotPrintedQty || item.qty || 0) > 0)
      .map((item) => {
        const kotPrintedQty = item.kotPrintedQty || item.qty || 0;
        const newQty = item.newQty !== undefined ? item.newQty : 0;
        return {
          ...item,
          kotPrintedQty,
          newQty,
          qty: item.qty,
        };
      });

    // Deduplicate/merge by item id + option
    const reducedItemsMap = new Map();
    [...reducedActiveItems, ...removedCartItems].forEach((it) => {
      const key = `${it.id}_${it.option?.label || ""}`;
      reducedItemsMap.set(key, it);
    });
    const allReducedItems = Array.from(reducedItemsMap.values());

    // 3. Items added or qty increased (qty > kotPrintedQty)
    const increasedItems = existingItems
      .filter((item) => (item.qty || 0) > (item.kotPrintedQty || 0))
      .map((item) => ({
        ...item,
        qty: (item.qty || 0) - (item.kotPrintedQty || 0),
      }));

    const hasPriorKot = existingItems.some((item) => (item.kotPrintedQty || 0) > 0) || allReducedItems.length > 0;
    return {
      allReducedItems,
      increasedItems,
      hasPriorKot,
    };
  }

  async dispatchKotSlips({
    sale,
    fallbackPrintFunction = () => {},
    categories = [],
    titles = [],
    allReducedItems = [],
    increasedItems = [],
    hasPriorKot = false,
  }) {
    if (!hasPriorKot) {
      // Initial KOT Print
      await this.printKot({
        sale,
        fallbackPrintFunction,
        categories,
        titles: titles || [],
        notes: [],
      });
      return;
    }

    const categoryMap = categories.reduce((map, category) => {
      map[String(category.id)] = category;
      return map;
    }, {});

    const terminalSettings = TerminalHelper.getTerminalSettings();
    const splitOption = terminalSettings.kotSplitOptions;

    // Group items by kitchen / printer destination
    const kitchenGroups = {};
    const getGroup = (key) => {
      if (!kitchenGroups[key]) {
        kitchenGroups[key] = {
          activeItems: [],
          reducedItems: [],
          increasedItems: [],
        };
      }
      return kitchenGroups[key];
    };

    (sale.items || []).forEach((saleItem) => {
      const key = getKitchenGroupKey(saleItem, categoryMap, splitOption);
      getGroup(key).activeItems.push(saleItem);
    });

    allReducedItems.forEach((saleItem) => {
      const key = getKitchenGroupKey(saleItem, categoryMap, splitOption);
      getGroup(key).reducedItems.push(saleItem);
    });

    increasedItems.forEach((saleItem) => {
      const key = getKitchenGroupKey(saleItem, categoryMap, splitOption);
      getGroup(key).increasedItems.push(saleItem);
    });

    // Dispatch slips per kitchen group
    for (const groupKey of Object.keys(kitchenGroups)) {
      const { activeItems, reducedItems, increasedItems: groupIncreasedItems } = kitchenGroups[groupKey];

      if (reducedItems.length > 0) {
        // 1. First slip: CANCELLED KOT slip for this kitchen's reduced items
        await this.printKot({
          sale: {
            ...sale,
            items: reducedItems,
          },
          fallbackPrintFunction,
          categories,
          titles: [KOT_TITLES.CANCELLED],
          notes: [],
          skipConsolidatedKOT: true,
          strock: true,
        });

        // 2. Second slip: KOT EDITED slip for this kitchen's active items (if any remain)
        if (activeItems.length > 0) {
          const reducedNotes = formatReducedItemsNotes(reducedItems);
          await this.printKot({
            sale: {
              ...sale,
              items: activeItems,
            },
            fallbackPrintFunction,
            categories,
            titles: [KOT_TITLES.UPDATED_KOT],
            notes: reducedNotes,
            skipConsolidatedKOT: true,
          });
        }
      } else if (groupIncreasedItems.length > 0) {
        // ADDON KOT: Only new items or qty additions for this kitchen
        await this.printKot({
          sale: {
            ...sale,
            kotTime: DateTimeHelpers.getLocalISOString(),
            items: groupIncreasedItems,
          },
          fallbackPrintFunction,
          categories,
          titles: titles.length ? titles : [KOT_TITLES.ADDON],
          notes: [],
          skipConsolidatedKOT: true,
        });
      }
      // If reducedItems.length === 0 and groupIncreasedItems.length === 0:
      // No changes for this kitchen -> Do not print anything
    }

    // Consolidated KOT dispatch if enabled
    if (terminalSettings.consolidatedKOT) {
      const invoiceFormat = terminalSettings.kotFormat || INVOICE_FORMATS.THREE_INCH;
      const defaultKotIp = terminalSettings.kotWithInvoiceIp || "";
      const bridge = AppBridgeHelper.getBridge();

      if (bridge && invoiceFormat !== INVOICE_FORMATS.A4) {
        if (allReducedItems.length > 0) {
          await printConsolidatedKotIfNeeded(
            { ...sale, items: allReducedItems, kotTime: DateTimeHelpers.getLocalISOString() },
            false,
            terminalSettings,
            invoiceFormat,
            defaultKotIp,
            [],
            [KOT_TITLES.CONSOLIDATED, KOT_TITLES.CANCELLED],
            true,
          );

          if ((sale.items || []).length > 0) {
            const allReducedNotes = formatReducedItemsNotes(allReducedItems);
            await printConsolidatedKotIfNeeded(
              { ...sale, kotTime: DateTimeHelpers.getLocalISOString() },
              false,
              terminalSettings,
              invoiceFormat,
              defaultKotIp,
              allReducedNotes,
              [KOT_TITLES.CONSOLIDATED, KOT_TITLES.UPDATED_KOT],
            );
          }
        } else if (increasedItems.length > 0) {
          await printConsolidatedKotIfNeeded(
            { ...sale, items: increasedItems, kotTime: DateTimeHelpers.getLocalISOString() },
            false,
            terminalSettings,
            invoiceFormat,
            defaultKotIp,
            [],
            [KOT_TITLES.CONSOLIDATED, KOT_TITLES.ADDON],
          );
        }
      }
    }
  }

  async processKOT({ activeSale, categories = [], fallbackPrintFunction, titles = [] }) {
    if (!activeSale) return { success: false, message: "No active sale" };

    const { allReducedItems, increasedItems, hasPriorKot } = this.extractKotChanges(activeSale);

    if (increasedItems.length === 0 && allReducedItems.length === 0) {
      return {
        success: true,
        message: KOT_MESSAGES.NO_CHANGES,
        updatedItems: activeSale.items,
        shouldIncrementToken: false,
      };
    }

    const { token_no, shouldIncrementToken } = this.handleTokenAssignment(activeSale);

    const baseSale = {
      ...activeSale,
      tax: (activeSale.cess || 0) + (activeSale.tax || 0),
      token_no: token_no,
    };

    try {
      await this.dispatchKotSlips({
        sale: baseSale,
        fallbackPrintFunction,
        categories,
        titles,
        allReducedItems,
        increasedItems,
        hasPriorKot,
      });

      const updatedItems = (activeSale.items || []).map((si) => ({
        ...si,
        kotPrintedQty: si.qty,
        status: KOT_STATUS.DRAFT,
      }));

      await SalesHelper.updateSaleKotStatus(activeSale.local_id, KOT_STATUS.PENDING, {
        itemUpdates: updatedItems,
        deletedItems: [],
      });

      return {
        success: true,
        updatedItems,
        kot_status: KOT_STATUS.PENDING,
        token_no: token_no,
        shouldIncrementToken,
        saleToPrint: {
          ...baseSale,
          items: updatedItems,
          deletedItems: [],
        },
      };
    } catch (err) {
      console.error("KOT Error:", err);
      return { success: false, message: KOT_MESSAGES.FAILED };
    }
  }

  async printRemainingKotSafe({ sale, kotPrintRequired = true, titles = [] }) {
    try {
      await this.printRemainingKot({ sale, kotPrintRequired, titles });
    } catch (error) {
      console.error("Failed to print KOT invoice:", error);
    }
  }

  async printRemainingKot({ sale, kotPrintRequired = true, titles = [] }) {
    const { allReducedItems, increasedItems, hasPriorKot } = this.extractKotChanges(sale);

    if (!increasedItems.length && !allReducedItems.length) {
      return;
    }

    const { token_no = null } = this.handleTokenAssignment(sale);

    const baseSale = {
      ...sale,
      token_no: token_no,
    };

    if (kotPrintRequired !== false) {
      const categories = await ProductHelper.getCategoriesFromDB();
      await this.dispatchKotSlips({
        sale: baseSale,
        fallbackPrintFunction: () => {},
        categories,
        titles,
        allReducedItems,
        increasedItems,
        hasPriorKot,
      });
    }

    const updatedSale = {
      ...sale,
      token_no: token_no,
      items: sale?.items.map((saleItem) => ({ ...saleItem, kotPrintedQty: saleItem.qty, latestNote: null })),
      deletedItems: [],
    };
    await SalesHelper.saveSaleInDB(updatedSale, { notify: true });
  }

  async performPrintKotProcess({ sale, options, titles, kotPrintRequired }) {
    let saleToPrint = sale;
    let token_no = sale.token_no;

    if (!token_no) {
      const { token_no: assignedToken } = this.handleTokenAssignment(sale);
      token_no = assignedToken;
      saleToPrint = {
        ...sale,
        token_no: token_no,
      };
    }

    if (options.selectedTable) {
      saleToPrint = {
        ...saleToPrint,
        table: options.selectedTable,
      };
    }

    if (kotPrintRequired !== false) {
      const categories = await ProductHelper.getCategoriesFromDB();
      await this.printKot({
        sale: saleToPrint,
        fallbackPrintFunction: () => {},
        categories: categories,
        titles,
      });
    }

    const updatedSale = {
      ...sale,
      token_no: token_no,
      deletedItems: [],
      items: sale.items.map((saleItem) => ({ ...saleItem, kotPrintedQty: saleItem.qty, latestNote: null })),
    };
    await SalesHelper.saveSaleInDB(updatedSale, { notify: true });
  }

  async printKotSafe({
    sale,
    fallbackPrintFunction,
    categories,
    titles = [],
    notes,
    kotPrintRequired = true,
    showItems,
  }) {
    try {
      await this.printKot({ sale, fallbackPrintFunction, categories, titles, notes, kotPrintRequired, showItems });
    } catch (error) {
      console.error("Failed to print KOT invoice:", error);
    }
  }

  async printKot({
    sale,
    fallbackPrintFunction,
    categories = [],
    titles = [],
    notes = [],
    skipConsolidatedKOT = false,
    showItems = true,
    strock = false,
  }) {
    try {
      if (!categories || categories.length === 0) {
        categories = (await ProductHelper.getCategoriesFromDB()) || [];
      }

      const categoryMap = categories.reduce((map, category) => {
        map[String(category.id)] = category;
        return map;
      }, {});

      const filteredItems = (sale?.items || []).filter((item) => {
        const category = categoryMap[String(item.item?.category)];
        return ProductHelper.isKotPrintEnabledForCategory(category, sale.order_type);
      });

      if (filteredItems.length === 0 && notes.length === 0) {
        return;
      }

      const saleToPrint = {
        ...sale,
        kotTime: DateTimeHelpers.getLocalISOString(),
        items: filteredItems,
        notes,
        showItems,
      };

      const terminalSettings = TerminalHelper.getTerminalSettings();
      const defaultKotIp = terminalSettings.kotWithInvoiceIp || "";
      const invoiceFormat = terminalSettings.kotFormat || INVOICE_FORMATS.THREE_INCH;

      const bridge = AppBridgeHelper.getBridge();
      if (!bridge || invoiceFormat === INVOICE_FORMATS.A4) {
        if (fallbackPrintFunction) fallbackPrintFunction(invoiceFormat);
        return;
      }

      const printJobs = buildKotPrintJobs(terminalSettings.kotSplitOptions, saleToPrint, categoryMap, showItems);
      await executeKotPrintJobs(printJobs, saleToPrint, titles, invoiceFormat, terminalSettings, notes, strock);

      const consolidatedTitles = titles.includes(KOT_TITLES.TABLE_CHANGED)
        ? [KOT_TITLES.TABLE_CHANGED, KOT_TITLES.CONSOLIDATED]
        : [KOT_TITLES.CONSOLIDATED];

      return await printConsolidatedKotIfNeeded(
        saleToPrint,
        skipConsolidatedKOT,
        terminalSettings,
        invoiceFormat,
        defaultKotIp,
        notes,
        consolidatedTitles,
        strock,
      );
    } catch (err) {
      console.error("Print failed:", err);
    }
  }

  async printTestKot({ kitchenIp, kitchenName, categoryName }) {
    const trimmedIp = kitchenIp?.trim();
    if (!trimmedIp) {
      throw new Error("No Ip for the kitchen");
    }

    const bytes = InvoiceHelper.getTestKotReceipt({
      categoryName,
      kitchenName,
      kitchenIp: trimmedIp,
    });

    const content = bytesToBase64(bytes);

    await PrinterManager.print({
      content,
      printerType: PRINTER_TYPES.LAN,
      printerTarget: trimmedIp,
      label: `KOT TEST - ${categoryName}`,
    });
  }
}

const kotManager = new KotManager();
export default kotManager;
